import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/cn';

/**
 * Font weight shared by `TableRow`, `TableHead` and `TableCell`. `inherit` sets no weight.
 */
type TableWeight = 'inherit' | 'medium' | 'semibold' | 'bold';

const weightStyles = {
  inherit: '',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
} satisfies Record<TableWeight, string>;

const tableVariants = cva('w-full caption-bottom', {
  variants: {
    textSize: {
      sm: 'text-sm',
      md: 'text-base',
    },
    dividers: {
      default: '',
      muted: '[&>*>tr]:border-border-muted',
      strong: '[&>*>tr]:border-border-default',
    },
    inset: {
      default: '',
      edges: '[&>*>tr>:first-child:not([colspan])]:ps-4 [&>*>tr>:last-child:not([colspan])]:pe-4',
      md: '[&>*>tr>td]:px-6 [&>*>tr>th]:px-6 [&>*>tr>th]:py-3',
      lg: '[&>*>tr>td]:px-8 [&>*>tr>th]:px-8 [&>*>tr>th]:py-4',
    },
  },
  defaultVariants: {
    textSize: 'sm',
    dividers: 'default',
    inset: 'default',
  },
});

/**
 * Table props. `inset` widens the cell padding for a table that fills a card
 * edge to edge, so the first and last columns do not touch the card border.
 * `inset="edges"` pads only the first and last column, for a table inside a
 * bordered box. A cell with `colSpan` keeps its own padding, so a full-width
 * row such as an inline picker stays flush.
 */
function Table({
  className,
  textSize,
  dividers,
  inset,
  ...props
}: React.ComponentProps<'table'> & VariantProps<typeof tableVariants>) {
  return (
    <div data-slot="table-container" className="relative w-full overflow-x-auto">
      <table
        data-slot="table"
        className={cn(tableVariants({ textSize, dividers, inset }), className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead data-slot="table-header" className={cn('[&_tr]:border-b', className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

const tableFooterVariants = cva('border-t font-medium [&>tr]:last:border-b-0', {
  variants: {
    variant: {
      default: 'bg-muted/50',
      total: 'border-border-default bg-white',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

function TableFooter({
  className,
  variant,
  ...props
}: React.ComponentProps<'tfoot'> & VariantProps<typeof tableFooterVariants>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(tableFooterVariants({ variant }), className)}
      {...props}
    />
  );
}

const tableRowVariants = cva('has-aria-expanded:bg-muted/50 data-[state=selected]:bg-muted', {
  variants: {
    hover: {
      default: 'hover:bg-muted/50 transition-colors',
      none: '',
      strong: 'transition-colors hover:bg-neutral-200',
    },
    divider: {
      true: 'border-b',
      false: '',
    },
    weight: weightStyles,
  },
  defaultVariants: {
    hover: 'default',
    divider: true,
    weight: 'inherit',
  },
});

function TableRow({
  className,
  hover,
  divider,
  weight,
  ...props
}: React.ComponentProps<'tr'> & VariantProps<typeof tableRowVariants>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(tableRowVariants({ hover, divider, weight }), className)}
      {...props}
    />
  );
}

const tableHeadVariants = cva(
  'text-foreground h-10 px-2 text-left align-middle whitespace-nowrap has-[[role=checkbox]]:pr-0',
  {
    variants: {
      padding: {
        default: '',
        none: 'px-0',
      },
      weight: weightStyles,
    },
    defaultVariants: {
      padding: 'default',
      weight: 'medium',
    },
  },
);

function TableHead({
  className,
  padding,
  weight,
  ...props
}: React.ComponentProps<'th'> & VariantProps<typeof tableHeadVariants>) {
  return (
    <th
      data-slot="table-head"
      className={cn(tableHeadVariants({ padding, weight }), className)}
      {...props}
    />
  );
}

const tableCellVariants = cva('p-2 align-middle has-[[role=checkbox]]:pr-0', {
  variants: {
    padding: {
      default: '',
      none: 'p-0',
    },
    weight: weightStyles,
    truncate: {
      true: 'truncate',
      false: 'whitespace-nowrap',
    },
  },
  defaultVariants: {
    padding: 'default',
    weight: 'inherit',
    truncate: false,
  },
});

function TableCell({
  className,
  padding,
  weight,
  truncate,
  ...props
}: React.ComponentProps<'td'> & VariantProps<typeof tableCellVariants>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(tableCellVariants({ padding, weight, truncate }), className)}
      {...props}
    />
  );
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell };
